import { Component, Vue, Watch } from "vue-facing-decorator";
import Bubble from "../components/bubble.vue";
import smoothscroll from "smoothscroll-polyfill";
import { __vueEvent, __oneMinute } from "../constant";
import { EVENTS } from "../enums/events";
import { IConfig } from "../interfaces/config";
import Loader from "../components/loader.vue";
import QuickReply from "../components/quick_reply.vue";
import TypingIndicator from "../components/typing_indicator.vue";
import ProcessingMessage from "../components/processing_message.vue";
import LoginModal from "../components/login_modal.vue";
import TranscriptModal from "../components/footer-buttons/transcript_modal.vue";
import ConsentModal from "../components/consent_modal.vue";
import Modal from "../components/modal.vue";
import Card from "../components/card.vue";
import { STORE_ACTION, STORE_MUTATION, CACHE_ID_TYPE, MESSAGE_STRUCTURE_TYPE, MESSAGE_TYPE, CONSTANTS } from "../enums";
import { IMessage } from "../interfaces";
import { ChatService } from "../service/chat_service";
import { StorageHelper, getMessageFromServer, sanitizeHtml, sanitizeToText } from "../helpers";
import Banner from "../components/banner.vue";
import { isMobile } from "../helpers/is_mobile";
import { Util } from "../util";
import { UserService } from "../service/user_service";
import { FeedbackRating, MessageContentSettings, MessageStructure, ScrollPosition } from '../types';
import LayoutCarousel from "../components/layout-carousel.vue";
import MessageFeedback from "../components/chat/message_feedback.vue";
import FeedbackModal from "../components/chat/feedback_modal.vue";
import { AvatarDisplayMode, SenderDisplayMode, SenderDisplaySettingsConfig } from "../types/sender_display";

@Component({
  components: {
    Bubble,
    Loader,
    QuickReply,
    TypingIndicator,
    Card,
    Banner,
    ProcessingMessage,
    Modal,
    LoginModal,
    TranscriptModal,
    ConsentModal,
    LayoutCarousel,
    MessageFeedback,
    FeedbackModal
  },
})
export default class Chat extends Vue {
    //#region Class properties
    //isUserHasAvatar = false;

    isPageLoaded = false;

    hideTagLine = false;

    isPrevMessageFetching = false;
    /**
     * JIRA SD-10864: Holds the current message to be announced by the screen reader.
     * Tied to a visually hidden aria-live region to support macOS VoiceOver.
     */
    a11yAnnouncement: string = "";
    
    /**
     * JIRA SD-10864: Stores the ID of the last processed message.
     * Prevents duplicate announcements and ensures the watcher correctly identifies 
     * new additions in deep-watched arrays.
     */
    lastAnnouncedMessageId: string = "";

    anchorScrollOffset = 0;
    resetSpacer = true;

		// Feedback modal state
		showFeedbackModal = false;
		feedbackModalData: any = null;

		//#endregion

		//#region Computed Properties

    get isMobileActive() {
      return isMobile.any() || this.$store.state.isIFrameLoaded;
    }

    get showTypingIndicator() {
      return this.$store.state.showTypingIndicator || false;
    }

    get messages() {
      return this.$store.state.messages || [];
    }

    get isBotHasAvatar() {
      return this.$store.state.isBotHasAvatar || false;
    }

    get shouldShowBanner() {
      return this.$store.state.shouldShowBanner || false;
    }

    get isUserHasAvatar() {
      return this.$store.state.isUserHasAvatar || false;
    }

    get showProcessingMessage() {
      return this.$store.state.showProcessingMessage || false;
    }

    get showAdminLoginModal() {
      return this.$store.state.showAdminLoginModal || false;
    }

    get showConsentModal() {
      return this.$store.state.showConsentModal || false;
    }

    get showWelcomeBanner() {
      return this.$store.state.showWelcomeBanner || false;
    }

    get enableMessageNavigation() {
      return this.$store.state.enableMessageNavigation || false;
    }

		get showTranscriptModal() {
      return this.$store.state.showTranscriptModal || false;
    }

    //#endregion

    mounted() {
        // initiate scroll polyfill
        smoothscroll.polyfill();
        this.listenEvents();
    }

    updated() {
        if (typeof jQuery !== 'undefined') {
            if (this.$store.state.isIFrameLoaded) {
                this.$nextTick(function () {
                    this.trapFocus();
                });
            }
        }
    }

    /**
     * SD-5153: Returns the message prefix based on the given message type.
     *
     * @param messageType - The type of the message.
     * @returns The message prefix.
     */
    getMessagePrefix(messageType: MESSAGE_TYPE): string {
        try {
            switch (messageType) {
                case MESSAGE_TYPE.Bot:
                case MESSAGE_TYPE.Agent:
                    return 'Bot says: ';
                case MESSAGE_TYPE.User:
                    return 'I say: ';
                // case MESSAGE_TYPE.Agent:
                //     return 'Agent said: ';
                default:
                    return ''; // Handle other message types as needed
            }
        } catch (error) {
            //console.error('Error occurred:', error);
            return '';
        }
    }

    trapFocus() {
        var element = <HTMLElement>document.getElementsByClassName(`${this.$store.state.prefixClassName}standalone`)[0];

        //Last focusable element of popup
        var lastFocusableEl;
        if (this.$store.state.disableInput) {
            var focusableEls = element.querySelectorAll(`button:not([style*="display:none"]):not([style*="display: none"])`);
            var displayedFocusElement = [];
            for (var i = 0; i < focusableEls.length - 1; i++) {
                if (jQuery(focusableEls[i]).is(":visible")) {
                    displayedFocusElement.push(focusableEls[i]);
                }
            }
            lastFocusableEl = <HTMLElement>displayedFocusElement[displayedFocusElement.length - 1];
        } else {
            lastFocusableEl = jQuery(`button.${this.$store.state.prefixClassName}btn.${this.$store.state.prefixClassName}send`);
        }
        this.$store.commit(STORE_MUTATION.SetLastFocusableElement, lastFocusableEl);
    }

    listenEvents() {
        //@ts-ignore
        __vueEvent.on(EVENTS.ScrollChatToBottom, this.scrollChatToBottom);
        __vueEvent.on(EVENTS.ScrollChatToTop, this.scrollChatToTop);
        //@ts-ignore
        __vueEvent.once(EVENTS.SetConfig, (config: IConfig) => {
                this.isPageLoaded = true;
                this.hideTagLine = config.hideTagLine;
                this.checkAdminModeOnReload();
            });
        __vueEvent.on(EVENTS.ScrollToNewMessageTop, this.scrollToNewMessageTop);
        __vueEvent.on(EVENTS.ScrollFirstAgentToAnchor, this.scrollFirstAgentToAnchor);
        __vueEvent.on(EVENTS.ScrollTopToLastUserMessage, this.scrollTopToLastUserMessage);

        // Add feedback modal event listener
        __vueEvent.on(EVENTS.ShowFeedbackModal, this.handleShowFeedbackModal);
        __vueEvent.on(EVENTS.HideFeedbackModal, this.handleHideFeedbackModal);

        const fetchMessage = async () => {
            const element = this.chatBody; //(event.srcElement as HTMLElement)
            const previousScrollHeightMinusTop =
                element.scrollHeight - element.scrollTop;
            const msgs = await getMessageFromServer(
                this.$store.state.lastMessageId,
                CACHE_ID_TYPE.BeforeMessageCacheId,
                true,
                this.$store.state.minimumClientVersion || "",
                `chat.ts:fetchMessage`
            );
            setTimeout(() => {
                this.$store.dispatch(STORE_ACTION.AddPrevMessages, msgs);
                this.isPrevMessageFetching = false;
                setTimeout(() => {
                    if (msgs.length > 0) {
                        element.scrollTop =
                            element.scrollHeight - previousScrollHeightMinusTop;
                    } else {
                        element.scrollTop = 0;
                    }
                }, 0);
            }, 500);
        };
        this.chatBody.onscroll = async event => {
            const element = event.srcElement as HTMLElement;
            const scrollTop = element.scrollTop;
            if (
                this.isPrevMessageFetching === false &&
                scrollTop <= 10 &&
                this.$store.state.lastMessageId > 0
            ) {
                this.isPrevMessageFetching = true;
                const previousScrollHeightMinusTop =
                    element.scrollHeight - element.scrollTop;
                //this.$store.commit(STORE_MUTATION.AddFetchMsgStamp, "Loading previous messages");
                setTimeout(() => {
                    element.scrollTop = 0; //element.scrollHeight - previousScrollHeightMinusTop;
                }, 0);
                fetchMessage();
            }
        };
    }


    /**
     * The function takes a string representing a date and returns a Date object,
     * or the current date if the input is invalid.
     * @param {string} dateString - The dateString parameter is a string that represents a date.
     * @returns a Date object.
     */
    convertStringToDate(dateString: string) {
        let date = null;
        try {
            if (!dateString) {
                throw new Error("Input date string is empty or null.");
            }
            const dateObject = new Date(dateString);
            if (isNaN(dateObject.getTime())) {
                throw new Error("Invalid date representation.");
            }
            date = dateObject;
        } catch (ex) {
            date = null;
        }
        return date;
    }

    // Function to serialize the date object to a string
    serializeDate(date) {
        try {
            if (!date || isNaN(new Date(date).getTime())) {
              return ""; // Return an empty string if the date is empty, false, or not a valid Date object
            }

            return new Date(date).toISOString(); // Convert the date to an ISO 8601 string (e.g., "2023-07-01T12:34:56.789Z")
        } catch (error) {
            //console.error("Error occurred during date serialization:", error);
            return ""; // Return an empty string if there was an error during the conversion
        }
    }
    /**
     * Check if the Admin Mode is enabled on page reload
     */
    async checkAdminModeOnReload() {
        try {
            // Check if we have some admin mode related data saved in the local storage
            const extUserId = new UserService().getExtUserId();
            const deviceId = StorageHelper.get("browser_id") || "";
            const botId = this.$store.state.extBotId || 0;
            const pageId = this.$store.state.pageId || "";
            const adminModeValue = StorageHelper.get(`swc_is_adm_${pageId}_${extUserId}`) || "";
            var decodedString = window.atob( adminModeValue );
            const isAdminModeEnabled = decodedString == "1";

            if (!Util.isNullOrEmpty(extUserId) && !Util.isNullOrEmpty(deviceId)
                && !Util.isNullOrEmpty(botId.toString()) && isAdminModeEnabled) {
                // Yes, we have some data saved in the local storage
                // Make a call to the server to check if the user is still logged in
                await new UserService().checkUserStatus(botId, deviceId, extUserId);
            }

        } catch (ex) {
            console.log("[chat.ts] An error has occurred -> Inside checkAdminModeOnReload -> Error: ", ex);
        }
    }

    get chatBody() {
        return this.$refs.chat_body as HTMLElement;
    }

    /**
     * Gets the indicator text displayed when a message is being sent.
     * @returns The text to display while sending a message,
     * defaulting to a constant if not specified in page configuration.
     */
    get sendingIndicatorContent(): string {
      const pageConfig = this.$store.state.pageConfig;
      const sendingIndicatorText = pageConfig ? pageConfig.sendingIndicatorText : "";
      return sendingIndicatorText || CONSTANTS.SENDING_MESSAGE;
    }

		//#region Handle Transcript Modal Change

		/**
		 * The function returns a class name by concatenating a prefix with a given value.
		 * @param value - The value parameter is a string representing the class name
		 * @returns the concatenation of the value parameter and the prefixClassName property of the
		 * store.state object.
		 */
		getClassName(value) {
			try {
				return this.$store.state.prefixClassName + value;
			} catch (ex) {
				return value;
			}
		}

		@Watch("showTranscriptModal", { immediate: true })
		onPropertyChanged(value: any) {
			this.handleShowTranscriptModalChange(value);
		}

		/**
		 * SD-8649: Watches for changes in the visibility of the admin login modal.
		 */
		@Watch("showAdminLoginModal", { immediate: true })
		onAdminLoginModalChanged(value: any) {
			this.handleShowTranscriptModalChange(value);
		}

		/**
		 * SD-8438: Handles the change in visibility of the transcript modal.
		 *
		 * This method manages the addition or removal of a modal-specific CSS class to the document body
		 * based on whether the transcript modal is being shown or hidden. This behavior is specifically
		 * targeted at Safari browsers, iOS devices with WKWebView, or specific iOS scenarios where the
		 * application is not running in standalone mode or Safari.
		 *
		 * @param value - A boolean or truthy/falsy value indicating whether the transcript modal is being shown (true) or hidden (false).
		 */
		handleShowTranscriptModalChange(value: any) {
			try {
				let userAgentString = navigator.userAgent.toLowerCase();
				let safariIndex = userAgentString.indexOf("safari");

				// Check if the chat page is opened in an iOS WK Webview
				const standalone = "standalone" in navigator && navigator["standalone"];
				const safari = /safari/.test(userAgentString);
				const ios = /iphone|ipod|ipad|macintosh/.test(userAgentString);

				// SD-10057: Add the modal-show class to all normal browsers and iOS WK Webview
				if ((document.body != null) || (ios && !standalone && !safari)) {
					// If the modal is shown, add the class to the body
					if (value) {
						document.body.classList.add(this.getClassName("modal-show"));
					} else {
						// If the modal is hidden, remove the class from the body
						if (document.body.classList.contains(this.getClassName("modal-show"))) {
							document.body.classList.remove(this.getClassName("modal-show"));
						}
					}
				}
			} catch (ex) {
				console.log("[chat.ts] An error has occurred -> Inside handleShowTranscriptModalChange -> Error: ", ex);
			}
		}

		//#endregion

    /**
     * Normalizes the scroll position value to ensure it is one of the accepted values.
     */
    normalizeScrollPosition(scrollPosition: unknown): ScrollPosition {
        switch (scrollPosition) {
            case "top":
            case "near_top":
            case "default":
                return scrollPosition;
            default:
                return "default";
        }
    }

    /**
     * Scrolls to a specific anchor position in the chat body based on the provided offset and scroll position.
     */
    scrollToAnchorPosition(targetOffsetTop: number, scrollPosition: unknown) {
        try {
            const normalizedScrollPosition = this.normalizeScrollPosition(scrollPosition);
            if (normalizedScrollPosition !== "top" && normalizedScrollPosition !== "near_top") {
                this.resetSpacer = true;
                return;
            }

            const element = this.chatBody;
            if (!element) {
                this.resetSpacer = true;
                return;
            }

            const modeOffset = normalizedScrollPosition === "near_top" ? 64 : 6;
            const desiredTop = Math.max(targetOffsetTop - modeOffset, 0);
            //const maxScroll = Math.max(element.scrollHeight - element.clientHeight, 0);
            this.resetSpacer = false;
            this.anchorScrollOffset = 200;

            //const isIFrameLoaded = this.$store.state.isIFrameLoaded;
            //const isTilesOpened = this.$store.state.isTilesOpenedInIframe;

            // Yes, the current browser supports native smooth scroll
            setTimeout(() => {
              if (element != null) {
                // Check if the current browser is Mac Computers or iOS Devices
                if (!this.isMacLike()) {
                  element.scroll({ top: desiredTop, behavior: "smooth" });
                } else {
                  // Current device is an iMac, Macbook, iPhone, iPod, or iPad
                  this.scrollToElement(element, desiredTop, 200);
                }
              }
            }, 10);
        } catch (ex) {
            this.resetSpacer = true;
        }
    }

    /**
     * Scrolls the chat to the anchor of the first agent message after the latest user message.
     */
    scrollFirstAgentToAnchor(payload: { scrollPosition?: unknown } = {}) {
      try {
        const normalizedScrollPosition = this.normalizeScrollPosition(payload.scrollPosition);
        if (normalizedScrollPosition !== "top" && normalizedScrollPosition !== "near_top") {
          this.resetSpacer = true;
          return;
        }

        let offsetTop = 0;
        const $lastUserMessageElem = jQuery(".satisfi_messages section.satisfi_message.satisfi_user:visible:last").next(
          ".satisfi_bot"
        );
        if ($lastUserMessageElem && $lastUserMessageElem.length && $lastUserMessageElem[0] != null) {
          offsetTop = $lastUserMessageElem[0].offsetTop;
        }

        this.scrollToAnchorPosition(offsetTop, normalizedScrollPosition);
      } catch (ex) {
        this.resetSpacer = true;
      }
    }

    scrollChatToTop() {
        this.isPrevMessageFetching = true;
        setTimeout(() => {
            const element = this.chatBody;
            this.resetSpacer = true;
            element.scroll({ top: 0 });
        }, 10);
    }

    /**
     * Detect if the system is Mac Computers or iOS Devices
     * like iMac, Macbook, iPhone, iPod, or iPad.
     * @returns {boolean} true if the system is Mac Computers or iOS Devices, false otherwise.
     */
    isMacLike() {
        try {
            const userAgent = navigator.userAgent || 'unknown';
            const appleDeviceRegex = /Mac|iPhone|iPod|iPad/i;
            const isMacLike = appleDeviceRegex.test(userAgent);
            return isMacLike;
        } catch (ex) {
            return false;
        }
    }

    /**
     * Scroll to the destination element by the duration specified.
     * @param {HTMLElement} scrollLayer - HTMLElement - The element that you want to scroll.
     * @param {number} destination - The position to scroll to.
     * @param {number} duration - The duration of the scroll animation in milliseconds.
     */
    scrollToElement(scrollLayer: HTMLElement, destination: number, duration: number) {
        try {
            if (duration <= 0) return;
            const difference = destination - scrollLayer.scrollTop;
            const perTick = (difference / duration) * 10;

            setTimeout(() => {
                scrollLayer.scrollTop = scrollLayer.scrollTop + perTick;
                if (scrollLayer.scrollTop === destination) return;
                this.scrollToElement(scrollLayer, destination, duration - 10);
            }, 10);
        } catch (ex) {
            return false;
        }
    }

    scrollToNewMessageTop() {
		try {
			this.resetSpacer = true;

			// detect support for the behavior property in ScrollOptions
			const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

			// SD-1671
			// Fix for Tiles Scrolling Issue In Chat
			// 1. Check if the chat is loaded inside the iframe
			const isIFrameLoaded = this.$store.state.isIFrameLoaded;
			const isTilesOpened = this.$store.state.isTilesOpenedInIframe;

			// Yes, the current browser supports native smooth scroll
			setTimeout(() => {
				const element = this.chatBody;
				if (element != null) {

					// Get the offsetTop value for the last message
					let offsetTop = 0;
					try {
						let $lastMessageElem = jQuery(".satisfi_messages section.satisfi_message:visible:last");
						// Check if an element exists in the visible DOM
						if ($lastMessageElem && $lastMessageElem.length && $lastMessageElem[0] != null) {
							offsetTop = $lastMessageElem[0].offsetTop;
						}
					} catch (ex2) {
						offsetTop = 0;
					}

					// SD-2838
                    // Check if the current browser is Mac Computers or iOS Devices
                    if (!this.isMacLike()) {
						// Check if this page is inside an iframe and also a Tile
						if (!!isIFrameLoaded && !!isTilesOpened) {
							// Fix for scrollIntoView() causing the whole page to move, when opened inside a Tile
							element.scroll({ top: offsetTop, behavior: "smooth" });
						} else {
							// continue with default behavior

                            // SD-3057
                            // Fix for Webchat UI Issue when Grammarly extension is installed
							jQuery(".satisfi_messages section:last")[0].scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'start'
                            });
						}
					} else {
						// Using `true` here
						// as Safari supports scrollIntoView with boolean parameter, but not smooth behavior option
						//jQuery(".satisfi_messages section:last")[0].scrollIntoView(true);

                        // Current device is an iMac, Macbook, iPhone, iPod, or iPad
                        // custom function for iOS
                        this.scrollToElement(element, offsetTop, 200);
					}

				}
				element.style.height = "500px";
			}, 10);

		} catch (ex) {
			//console.log('Error: ', (ex as Error).message);
		}
    }

    scrollTopToLastUserMessage() {
		try {

			// detect support for the behavior property in ScrollOptions
			const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

			// SD-1671
			// Fix for Tiles Scrolling Issue In Chat
			// 1. Check if the chat is loaded inside the iframe
			const isIFrameLoaded = this.$store.state.isIFrameLoaded;
			const isTilesOpened = this.$store.state.isTilesOpenedInIframe;

			// Yes, the current browser supports native smooth scroll
			setTimeout(() => {
				const element = this.chatBody;
				if (element != null) {

					// Get the offsetTop value for the last message
					let offsetTop = 0;
					try {
						let $lastMessageElem = jQuery(".satisfi_messages section.satisfi_message.satisfi_user:visible:last");
						// Check if an element exists in the visible DOM
						if ($lastMessageElem && $lastMessageElem.length && $lastMessageElem[0] != null) {
							offsetTop = $lastMessageElem[0].offsetTop;
						}
					} catch (ex2) {
						offsetTop = 0;
					}

					// SD-2838
                    // Check if the current browser is Mac Computers or iOS Devices
                    if (!this.isMacLike()) {
						// Check if this page is inside an iframe and also a Tile
						if (!!isIFrameLoaded && !!isTilesOpened) {
							// Fix for scrollIntoView() causing the whole page to move, when opened inside a Tile
							element.scroll({ top: offsetTop, behavior: "smooth" });
						} else {
							// continue with default behavior

                            // SD-3057
                            // Fix for Webchat UI Issue when Grammarly extension is installed
							jQuery(".satisfi_messages section.satisfi_user:last")[0].scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'start'
                            });
						}
					} else {
						// Using `true` here
						// as Safari supports scrollIntoView with boolean parameter, but not smooth behavior option
						//jQuery(".satisfi_messages section:last")[0].scrollIntoView(true);

                        // Current device is an iMac, Macbook, iPhone, iPod, or iPad
                        // custom function for iOS
                        this.scrollToElement(element, offsetTop, 200);
					}

				}
				element.style.height = "500px";
			}, 10);

		} catch (ex) {
			//console.log('Error: ', (ex as Error).message);
		}
    }

    scrollChatToBottom(schollSoothly = true) {
		try {
			this.resetSpacer = true;
			const element = this.chatBody;
			if (schollSoothly) {
				setTimeout(() => {
					if (element != null) {
						element.scroll({ top: element.scrollHeight, behavior: 'smooth' });
					}
					element.style.height = '500px';
				}, 10);
			}
			else {
				// Check if the system is Mac Computers or iOS Devices
				if(!this.isMacLike()) {
					// Its a non-Mac device
					element.scroll({ top: element.scrollHeight });
				} else {
					// Yes, its either a Mac/iOS Devices
					this.$nextTick(function () {
						element.scrollTop = element.scrollHeight;
					});
				}
			}
		} catch (ex) {
			//console.log('Error: ', (ex as Error).message);
		}
    }

    async retryMessage(msgId) {
        const msg: IMessage = this.$store.state.messages.find(
            msg => msg.id === msgId
        );
        try {
            //On retry button click remove retry/cancel link by making status "ok".
            this.$store.commit(STORE_MUTATION.UpdateMessageStatus, {
                id: msgId,
                status: "ok"
            } as IMessage);

            let newMessageResponse;
            //Check failed message is text with button or normal text message.
            if (msg.buttonMessageStructure != null) {
                newMessageResponse = await new ChatService().sendMessage(
                    msg.buttonMessageStructure
                );
                //make it button message structure null if message is send successful.
                this.$store.commit(STORE_MUTATION.UpdateMessageStructure, {
                    id: msgId,
                    buttonMessageStructure: null
                } as IMessage);
            } else {
                newMessageResponse = await new ChatService().sendMessage({
                    content: msg.contents.structures[0].components[0].content,
                    type: "text"
                });
            }

            //On message send update message id from Guid to messageCacheId sent from EBM.
            this.$store.commit(STORE_MUTATION.UpdateMessageCacheID, {
                id: msgId,
                newId: JSON.parse(newMessageResponse).recordInfo.messageCacheId
            } as IMessage);

        } catch (ex) {
            //On message fail make status of that message as "failed".
            this.$store.commit(STORE_MUTATION.SetRequestResponseReceived, true);
            this.$store.commit(STORE_MUTATION.UpdateMessageStatus, {
                id: msgId,
                status: "failed"
            } as IMessage);

            //onExceptionOccured("[chat.ts] An error has occurred -> Inside retryMessage -> Error: ", ex);
        }
    }

    cancelMessage(id) {
        this.$store.commit(STORE_MUTATION.RemoveMessage, id);
        this.$store.commit(STORE_MUTATION.RemoveMessage, id + MESSAGE_STRUCTURE_TYPE.Time);
    }

	/**
	 * Check if the current message is a card type
	 */
	isBotMessageCard(message: IMessage): boolean {
		try {
			// Check if the contents & structures have a valid value
			if (message.contents && message.contents.structures) {
				// Check if any of the structure have a type of "card"
				return message.contents.structures.some((s) => s.type == 'card');
			}
			return false;
		} catch (ex) {}
		return false;
	}

  //#region Methods for handling chat bubble display logic

  /**
   * Gets an array of valid component types supported by the chat interface.
   * @returns {string[]} An array containing the following valid component types
   */
  get validComponentTypes(): string[] {
    return ["text", "html", "markdown", "button", "link", "image", "line", "break"];
  }

  /**
   * SD-6819: Determines if a message structure should be displayed as a chat bubble.
   * @param structure - The message structure to evaluate
   * @returns {boolean} True if the structure should be shown as a chat bubble, false otherwise
   *
   * @remarks
   * Returns true if:
   * - The structure is a 'bubble' type
   * - An error occurs during evaluation
   *
   * Returns false if:
   * - The structure is invalid/null/undefined
   * - The components array is empty or invalid
   * - Any component has an invalid type
   */
  isShowChatBubble(structure: MessageStructure): boolean {
		try {
			// Check if the structure is valid
			if (!structure || typeof structure !== "object") return false;
			const { components } = structure;

			// Check if the structure is a bubble type and has valid components
			if (!Array.isArray(components) || components.length === 0) return false;

			// Check if all components are invalid
			const allInvalid = components.every((component) => {
				if (!component || typeof component.type !== "string") {
					return true; // Component is invalid or type is not a string
				}
				const typeLowerCase = component.type.toLowerCase();
				const isValidType = this.validComponentTypes.includes(typeLowerCase);
				return !isValidType; // Return true if the type is invalid
			});

			// Hide the bubble if all components or single component is invalid
			return !allInvalid;
		} catch (error) {
			//console.error('Error in isShowChatBubble:', error);
			return true; // Default value
		}
	}

  //#endregion

	/**
	 * JIRA - SD-5915
	 * Determines if the provided message object is a valid carousel or list layout.
	 *
	 * JIRA - SD-7651
	 * Checks for layout = "carousel" or "list" and component.type === "carousel" (To exclude the bubble message on top of carousel)
	 *
	 * @param {IMessage} message - The message object to validate.
	 * @param {MessageStructure} structure - The messageStructure object to validate.
	 * @returns {boolean} `true` if the message is valid and has a layout of either 'carousel' or 'list', otherwise `false`.
	 * @throws Logs an error to the console if an exception occurs during validation.
	 */
	isValidCarousel(message: IMessage, structure: MessageStructure) {
		let isValid = false
		try {
			isValid =  message && message.contents && message.contents.layout && (message.contents.layout=='carousel' || message.contents.layout=='list')
					  && structure && Array.isArray(structure.components) && structure.components.some(component => !Util.isNullOrEmpty(component.type) && component.type === "carousel")
		} catch (error) {
			console.error("'ClientApp/chat.ts' - Error while parsing the message object to valid Carousel. message = ", message)
		}

		return isValid
	}

  /**
   * SD-6270: Returns the alt label for the avatar based on the message type.
   * Reference: https://www.davidmacd.com/blog/alternate-text-for-css-background-images.html
   *
   * @param {MESSAGE_TYPE} messageType - The type of the message.
   * @returns {string} The alt label for the avatar.
   */
  getAvatarAltLabel(messageType: MESSAGE_TYPE): string {
		try {
			switch (messageType) {
				case MESSAGE_TYPE.Bot:
					return "AI Agent Avatar";
				case MESSAGE_TYPE.Agent:
					return "Live Agent Avatar";
				default:
					return ""; // Handle other message types as needed
			}
		} catch (error) {
			//console.error('Error occurred:', error);
			return "";
		}
	}

  /**
   * Determines if an alternative label for the avatar is required based on the message type and avatar settings.
   *
   * @param {IMessage} message - The message object to evaluate.
   * @returns {boolean} - Returns `true` if the avatar alternative label is required, otherwise `false`.
   *
   * The function checks if the message is valid and then evaluates whether the bot or agent avatar is enabled.
   * It returns `true` if either the bot avatar is enabled for a bot message or the agent avatar is enabled for a user message.
   * If an error occurs during the evaluation, it catches the exception and returns `false`.
   */
  isAvatarAltLabelRequired(message: IMessage): boolean {
    try {
      // Check if the message is valid
      if (!message) return false;

      // Check if the bot or agent avatar is enabled
      const { isBotHasAvatar, isUserHasAvatar } = this.$store.state;
      const showBotAvatar = isBotHasAvatar && message.type==0 && !this.isBotMessageCard(message);
      const showAgentAvatar = isUserHasAvatar && message.type==3;
      const isRequired = showBotAvatar || showAgentAvatar;
      return !!isRequired;
    } catch (ex) {
      //console.error('Error occurred:', ex);
      return false;
    }
  }

  async onMessageLinkClick(event: MouseEvent) {
    const target = event.target as HTMLAnchorElement;
    if (target && target.tagName === 'A' && target.href) {
        const chatService = new ChatService();
        // Post the inline click action to the server
        await chatService.inlineClickTracking(target.textContent || '', target.href);
        console.warn("Link clicked:", target.textContent, target.href);
    }
  }

	//#region Message Feedback

	/**
	 * Determine whether feedback UI (rating) should be shown for a message.
	 *
	 * Rules:
	 * - Only show feedback when `message.contents.settings.feedback.enableRating === true`.
	 * - Return `false` on any error or missing data.
	 *
	 * @param message - The IMessage object to check.
	 * @returns boolean - true if feedback UI should be shown, false otherwise.
	 */
	shouldShowFeedback(message?: IMessage): boolean {
		try {
			// Quick guard: must be an object
			if (!Util.isPlainObject(message)) return false;

			// `contents` expected to be an object per MessageContent type
			const contents = (message as IMessage).contents;
			if (!Util.isPlainObject(contents)) return false;

			// `settings` may be missing; guard it
			const settings = (contents as { settings?: MessageContentSettings }).settings;
			if (!Util.isPlainObject(settings)) return false;

			// `feedback` may be missing; guard it
			const feedback = (settings as MessageContentSettings).feedback;
			if (!Util.isPlainObject(feedback)) return false;

			// Only show feedback when enableRating is explicitly boolean true.
			// Avoids treating string "true" or other truthy values as enabled.
			return (feedback as { enableRating?: unknown }).enableRating === true;
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("[chat.ts] Error occurred while checking feedback visibility", ex);
			return false; // feedback not shown
		}
	}

	/**
   * Determines if feedback is in cooldown and user is not allowed to provide feedback.
   * @returns True if cooling down.
   */
	isFeedbackInCooldown(messageId: string): boolean {
		try {
			if (!messageId) return false;
			const cooldown = this.$store.state.feedbackCooldowns ?? {};
			if (typeof cooldown !== "object" || cooldown === null || !(messageId in cooldown)) return false;
			const cooldownEntry = cooldown[messageId];
			if (!cooldownEntry || !Util.isValidCooldownExpiry(cooldownEntry.expiry)) return false;
			return Date.now() < cooldownEntry.expiry;
		} catch {
			return false;
		}
	}

	/**
	 * Determines if a message currently has a pending rating operation.
	 * Use this to prevent duplicate requests and race conditions.
	 *
	 * @param message - The message to check for pending rating
	 * @returns `true` if the message has a pending rating, `false` otherwise
	 */
	isRatingPending(message: IMessage): boolean {
		try {
			if (!message) return false;
			return message?.feedback?.isRatingPending || false;
		} catch {
			return false;
		}
	}

	/**
	 * Retrieves the feedback rating for a given message.
	 *
	 * @param message - The message object to get the rating from
	 * @returns The feedback rating of the message, or FeedbackRating.Cleared if the message is null.
	 */
	currentMessageRating(message: IMessage): FeedbackRating {
		try {
			if (!message) return FeedbackRating.Cleared;
			return message?.feedback?.rating || FeedbackRating.Cleared;
		} catch {
			return FeedbackRating.Cleared;
		}
	}

  /**
   * Handles opening the feedback modal when thumbs down is clicked
   * @param data - Message data from the feedback component
   */
  handleShowFeedbackModal(data: any) {
    try {
      this.feedbackModalData = data;
      this.showFeedbackModal = true;
    } catch (ex) {
      console.error("[chat.ts] Error opening feedback modal:", ex);
    }
  }

  /**
   * Handles feedback modal close
   */
  handleHideFeedbackModal() {
    this.showFeedbackModal = false;
    this.feedbackModalData = null;
  }

	@Watch("showFeedbackModal", { immediate: true })
	onFeedbackModalChanged(value: any) {
		this.handleShowTranscriptModalChange(value);
	}

	//#endregion
     
    //#region Accessibility Watchers

    /**
     * JIRA SD-10864: Watches the messages array for new entries.
     * Uses deep: true to detect nested changes. Logic ensures only unique, 
     * new messages trigger a screen reader announcement.
     * * @param newVal - The updated array of chat messages.
     */
    @Watch("messages", { deep: true })
    onMessagesChanged(newVal: IMessage[]) {
        console.log("A11y Watcher Triggered - New Messages Length:", newVal.length);
        try {
            if (newVal && newVal.length > 0) {
                const lastMsg = newVal[newVal.length - 1];

                // Check if this specific message ID has already been announced
                if (lastMsg.id !== this.lastAnnouncedMessageId) {
                    this.lastAnnouncedMessageId = lastMsg.id; // Update tracker
                    
                    // Don't announce user's own messages (optional, based on preference)
                    // if (lastMsg.type === MESSAGE_TYPE.User) return;

                    this.announceMessage(lastMsg);
                }
            }
        } catch (ex) {
            console.error("[chat.ts] Error in a11y message watcher", ex);
        }
    }

    /**
     * JIRA SD-10864: Monitors the typing indicator state to provide 
     * real-time status updates to screen reader users.
     * * @param val - Boolean indicating if the bot is currently typing.
     */
    @Watch("showTypingIndicator")
    onTypingChanged(val: boolean) {
        if (val) {
            this.updateAnnouncement("Bot is typing...");
        }
    }

    /**
     * JIRA SD-10864: Monitors the processing message state (e.g., long-running API calls)
     * to ensure the user is aware the bot is still active.
     * * @param val - Boolean indicating if a processing state is active.
     */
    @Watch("showProcessingMessage")
    onProcessingChanged(val: boolean) {
        if (val) {
            const text = this.$store.state.processingDisplayText || "Processing your request...";
            this.updateAnnouncement(text);
        }
    }

    //#endregion

    //#region Accessibility Helpers

    /**
     * JIRA SD-10864: Updates the hidden aria-live region.
     * Uses a 100ms timeout to ensure the DOM has stabilized and the 
     * screen reader's buffer correctly registers the text change.
     * * @param text - The plain text string to be spoken by the screen reader.
     */
    updateAnnouncement(text: string) {
        setTimeout(() => {
            console.log("A11y Announcement Updated:", text);
            this.a11yAnnouncement = text;
        }, 100);
    }

    /**
     * JIRA SD-10864: Higher-level helper to trigger a message announcement.
     * Converts a complex message object into a simplified text summary.
     * * @param message - The IMessage object to be announced.
     */
    announceMessage(message: IMessage) {
        const text = this.generateA11yText(message);
        this.updateAnnouncement(text);
    }
    
    /**
     * JIRA SD-10864: Converts complex HTML structures into semantic "Spoken Text."
     * Replaces technical tags (ul, li, h, a) with descriptive words to ensure 
     * context is preserved in a text-only aria-live region.
     * * @param html - The raw HTML or Markdown string to be cleaned.
     * @returns A string optimized for speech synthesis.
     */
    sanitizeHtmlForSpeech(html: string): string {
        if (!html) return "";

        // 1. Sanitize first to ensure no hidden <script> or <style> blocks exist.
        // We explicitly forbid 'style' tags so CSS code isn't read aloud.
        let text = sanitizeHtml(html, { 
            FORBID_TAGS: ['style', 'script', 'svg', 'iframe', 'object'],
            ALLOWED_ATTR: ['href', 'src', 'alt'] // We only need content attributes
        });

        // 2. Handle Block Elements (Replace with pauses/periods)
        // This fixes the "HelloWorld" merging issue.
        // Replace closing div, p, br with a period and space.
        text = text.replace(/<\/(div|p|section|article|h[1-6])>/gi, '. '); 
        text = text.replace(/<br\s*\/?>/gi, '. ');

        // 3. Handle Lists (Semantic announcements)
        text = text.replace(/<ul[^>]*>/gi, ' List starts: ');
        text = text.replace(/<\/ul>/gi, ' List ends. ');
        text = text.replace(/<ol[^>]*>/gi, ' Numbered list starts: ');
        text = text.replace(/<\/ol>/gi, ' List ends. ');
        text = text.replace(/<li[^>]*>/gi, ' Bullet point: ');
        text = text.replace(/<\/li>/gi, '. ');

        // 4. Handle Headings
        text = text.replace(/<h[1-6][^>]*>/gi, ' Heading: ');

        // 5. Handle Links (Extract text only)
        // Matches <a href="..." style="...">Click Me</a> -> "Link: Click Me"
        text = text.replace(/<a[^>]*>(.*?)<\/a>/gi, ' Link: $1. ');

        // 6. Handle Images (Read Alt Text)
        // Matches <img src="..." alt="Description"> -> "Image: Description"
        text = text.replace(/<img[^>]*alt=["']([^"']*)["'][^>]*>/gi, ' Image: $1. ');

        // 7. Strip ALL remaining tags (Removes <span>, <b>, <i>, inline styles)
        // We replace with a single space to be safe against any remaining merging issues
        text = text.replace(/<[^>]+>/g, ' ');

        // 8. Decode HTML entities (e.g., &amp; -> &)
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        text = textarea.value;

        // 9. Cleanup multiple spaces/periods
        // "Hello . . World" -> "Hello. World"
        text = text.replace(/\s+/g, ' ').trim();
        text = text.replace(/\.\s*\./g, '.');

        return text;
    }

    /**
     * JIRA SD-10864: Core logic for generating accessible text from dynamic bot structures.
     * Processes bubbles, carousels, quick replies, and feedback buttons into 
     * a single scannable string for screen readers.
     * * @param message - The dynamic message structure from the chat stream.
     * @returns A flattened string representing the entire message content.
     */
    generateA11yText(message: IMessage): string {
        let text = "";

        try {
            // 1. Add Prefix (I say / Bot says)
            text += this.getMessagePrefix(message.type) + " ";

            // 2. Process Structures (Bubbles, Cards, etc)
            if (message?.contents?.structures && Array.isArray(message.contents.structures)) {
                message.contents.structures.forEach(structure => {
                    if (Array.isArray(structure?.components)) {
                        structure.components.forEach(comp => {
                            if (!comp || typeof comp !== 'object' || !comp.type) return;
                            
                            // Helper to clean content
                            let content = comp.content || "";

                            // Handle Markdown & HTML
                            if (comp.type === 'markdown') {
                                // Convert markdown to HTML first (using the global marked function)
                                // @ts-ignore
                                if (typeof marked !== 'undefined') { content = marked(content); }
                            }
                            
                            // Strip HTML tags for the announcer (we want the text, not the tags)
                            if (comp.type === 'html' || comp.type === 'markdown' || comp.type === 'text') {
                                const sanitizedHtml = sanitizeHtml(content);
                                text += this.sanitizeHtmlForSpeech(sanitizedHtml) + ". ";
                            }

                            // Handle Buttons & Links
                            if (comp.type === 'button' && comp.label) {
                                text += `Button: ${comp.label}. `;
                            }
                            if (comp.type === 'link' && comp.label) {
                                text += `Link: ${comp.label}. `;
                            }

                            // Handle Images
                            if (comp.type === 'image') {
                                const alt = comp.alt || "Image";
                                text += `${alt}. `;
                            }
                            
                            // Handle Breaks/Lines
                            // if (comp.type === 'break' || comp.type === 'line') {
                            //     text += ". "; // Pause
                            // }
                        });
                    }
                });
            }

            // 3. Process Quick Replies
            if (message?.contents?.quickReplies && Array.isArray(message.contents.quickReplies) && message.contents.quickReplies.length > 0) {
                const count = message.contents.quickReplies.length;
                // Explicitly announce they are quick replies
                text += `Quick Replies : `;
                
                // Read first few options to give context
                text += message.contents.quickReplies.map((qr) => `Button: ${qr?.label}`);
            }

            // 4. Check for Feedback
            if (this.shouldShowFeedback(message)) {
                text += "Button: Helpful. Button: UnHelpful. ";
            }

            /* // 5. Add Navigation Hint (Crucial for accessibility)
            // If the message has buttons/QR, tell the user how to get there.
            const hasInteractive = text.toLowerCase().includes("button") || text.toLowerCase().includes("link") || text.toLowerCase().includes("quick replies");
            
            if (hasInteractive && message.type !== 1) { // Don't add hint for user's own message
                text += " Press Shift plus Tab to interact with these options.";
            } */

        } catch (ex) {
            console.error("Error generating a11y text", ex);
            return text || "New message received";
        }

        return text;
    }

    //#endregion
  
	//#region Sender Display Methods

  /**
   * Determines if sender information can be shown for a message based on its type.
   *
   * @param message - The message to check
   * @returns true if: (type==0 or type==3) AND NOT a card message
   */
  canShowSenderByType(message: IMessage): boolean {
    try {
      if (!message) return false;
      const isAgentMessage = message.type === 0 || message.type === 3; // AI + Live agent
      const isNotCard = !this.isBotMessageCard(message);
      return isAgentMessage && isNotCard;
    } catch {
      return false;
    }
  }

  /**
   * Determines if sender display container should be shown for a message.
   *
   * @param message - The message to check
   * @returns true if: (type==0 or type==3) AND NOT a card message
   */
  shouldShowSenderDisplay(message: IMessage): boolean {
    try {
      if (!this.canShowSenderByType(message)) return false;

      const showAvatar = this.shouldShowSenderAvatar(message);
      const showName = this.shouldShowSenderName(message);

      // hide SenderDisplay only when BOTH are false
      return showAvatar || showName;
    } catch (ex) {
      return false;
    }
  }

  /**
   * Gets the display value based on mode, custom value, and default value.
   *
   * @param mode - The display mode ("hide", "custom", or "default")
   * @param customValue - The custom value to use if mode is "custom"
   * @param defaultValue - The default value to use if mode is "default"
   * @returns The resolved display value, or empty string if hidden
   */
  getDisplayValue(mode: string, customValue: string, defaultValue: string): string {
    if (mode === "hide") return "";
    if (mode === "custom") return customValue || "";
    return defaultValue || "";
  }

	/**
	 * Resolves sender display settings with defaults applied.
	 *
	 * @param message - The message containing settings
	 * @returns ResolvedSenderSettings with all values resolved
	 */
	resolveSenderDisplaySettings(message: IMessage): any {
		try {
			if (!message || !message.contents) {
				return { senderName: "", avatarUrl: "", showAvatar: false, showName: false };
			}

			const settings: SenderDisplaySettingsConfig = message.contents.settings?.senderDisplaySettings || {};
			const senderDisplayMode: SenderDisplayMode = settings.senderDisplay || "default";
			const avatarDisplayMode: AvatarDisplayMode = settings.avatarDisplay || "default";

			// Resolve sender name
			let senderName = this.getDisplayValue(
				senderDisplayMode,
				settings.customSenderName,
				this.$store.state.senderDisplayName
			);

			// Resolve avatar URL
			let avatarUrl = this.getDisplayValue(
				avatarDisplayMode,
				settings.customAvatarUrl,
				this.getDefaultAvatarUrl(message.type)
			);

			// Determine visibility flags
			const showAvatar = !Util.isNullOrEmpty(avatarUrl);
			const showName = !Util.isNullOrEmpty(senderName);

			return { senderName, avatarUrl, showAvatar, showName };
		} catch (ex) {
			return { senderName: "", avatarUrl: "", showAvatar: false, showName: false };
		}
	}

	/**
	 * Gets the sender name to display.
	 *
	 * @param message - The message containing sender settings
	 * @returns The resolved sender name, or empty string if hidden
	 */
	getSenderDisplayName(message: IMessage): string {
		try {
			const resolved = this.resolveSenderDisplaySettings(message);
			return resolved.senderName;
		} catch (ex) {
			return "";
		}
	}

	/**
	 * Gets the avatar URL to display.
	 *
	 * @param message - The message containing avatar settings
	 * @returns The resolved avatar URL, or empty string if hidden
	 */
	getSenderDisplayAvatarUrl(message: IMessage): string {
		try {
			const resolved = this.resolveSenderDisplaySettings(message);
			return resolved.avatarUrl;
		} catch (ex) {
			return "";
		}
	}

	/**
	 * Gets the default avatar URL based on message type from store.
	 *
	 * @param messageType - The message type (0=AI Agent, 3=Live Agent)
	 * @returns The avatar URL, or empty string if not available
	 */
	getDefaultAvatarUrl(messageType: number): string {
		try {
			if (messageType === 0) {
				return this.$store.state.aiAgentAvatarUrl || "";
			} else if (messageType === 3) {
				return this.$store.state.liveAgentAvatarUrl || "";
			}
			return "";
		} catch (ex) {
			return "";
		}
	}

	/**
	 * Determines if sender avatar should be displayed.
	 *
	 * @param message - The message containing avatar settings
	 * @returns True if avatar URL is available, false otherwise
	 */
	shouldShowSenderAvatar(message: IMessage): boolean {
		try {
			const avatarUrl = this.getSenderDisplayAvatarUrl(message);
			return !Util.isNullOrEmpty(avatarUrl);
		} catch (ex) {
			return false;
		}
	}

	/**
	 * Determines if sender name should be displayed.
	 *
	 * @param message - The message containing sender name settings
	 * @returns True if sender name is available, false otherwise
	 */
	shouldShowSenderName(message: IMessage): boolean {
		try {
			const senderName = this.getSenderDisplayName(message);
			return !Util.isNullOrEmpty(senderName);
		} catch (ex) {
			return false;
		}
	}

	//#endregion
  
}
